/*
 * SLIP encode/decode. Port of pc-nrf-dfu-js util/slip.js (originally MIT/GPL,
 * by Colin Clark 2017). Minimal TS rewrite; same wire behavior.
 */

const END = 0xc0;
const ESC = 0xdb;
const ESC_END = 0xdc;
const ESC_ESC = 0xdd;

export function encode(bytes: Uint8Array): Uint8Array {
  // Worst case: every byte expands to two, plus opening/closing END.
  const out = new Uint8Array(bytes.length * 2 + 2);
  let j = 0;
  out[j++] = END;
  for (let i = 0; i < bytes.length; i++) {
    const v = bytes[i];
    if (v === END) {
      out[j++] = ESC;
      out[j++] = ESC_END;
    } else if (v === ESC) {
      out[j++] = ESC;
      out[j++] = ESC_ESC;
    } else {
      out[j++] = v;
    }
  }
  out[j++] = END;
  return out.subarray(0, j);
}

export type SlipMessageHandler = (msg: Uint8Array) => void;

export class Decoder {
  private msgBuffer: Uint8Array;
  private msgBufferIdx = 0;
  private escape = false;
  private readonly onMessage: SlipMessageHandler;
  private readonly maxMessageSize: number;

  constructor(onMessage: SlipMessageHandler, opts?: { bufferSize?: number; maxMessageSize?: number }) {
    this.onMessage = onMessage;
    this.msgBuffer = new Uint8Array(opts?.bufferSize ?? 1024);
    this.maxMessageSize = opts?.maxMessageSize ?? 1024 * 1024;
  }

  decode(data: Uint8Array): void {
    for (let i = 0; i < data.length; i++) {
      let v = data[i];
      if (this.escape) {
        if (v === ESC_ESC) v = ESC;
        else if (v === ESC_END) v = END;
        // Any other value: per protocol it's invalid. Pass through (matches reference).
        this.addByte(v);
        continue;
      }
      if (v === ESC) {
        this.escape = true;
        continue;
      }
      if (v === END) {
        this.handleEnd();
        continue;
      }
      this.addByte(v);
    }
  }

  private addByte(v: number): void {
    if (this.msgBufferIdx >= this.msgBuffer.length) {
      const expanded = new Uint8Array(this.msgBuffer.length * 2);
      expanded.set(this.msgBuffer);
      this.msgBuffer = expanded;
    }
    if (this.msgBufferIdx >= this.maxMessageSize) {
      // Drop oversize message and reset.
      this.msgBufferIdx = 0;
      this.escape = false;
      return;
    }
    this.msgBuffer[this.msgBufferIdx++] = v;
    this.escape = false;
  }

  private handleEnd(): void {
    if (this.msgBufferIdx === 0) return; // toss opening END
    const msg = this.msgBuffer.slice(0, this.msgBufferIdx);
    this.msgBufferIdx = 0;
    this.onMessage(msg);
  }
}
